import React from 'react'
import PropTypes from 'prop-types'
import { InputTextarea } from 'primereact/inputtextarea'
import './WingsTextArea.css'

/**
 * WingsTextArea Component
 *
 * A multi-line textarea built on PrimeReact's InputTextarea, styled to match
 * the Wingspans design system. Supports optional label, helper text, error
 * state, and auto-resize behaviour.
 *
 * @component
 * @example
 * // Basic usage
 * <WingsTextArea
 *   label="Bio"
 *   placeholder="Tell us about yourself..."
 *   value={value}
 *   onChange={(e) => setValue(e.target.value)}
 * />
 *
 * @example
 * // Auto-resizing with error state
 * <WingsTextArea
 *   label="Description"
 *   autoResize
 *   rows={3}
 *   value={description}
 *   onChange={(e) => setDescription(e.target.value)}
 *   error="Description is required"
 * />
 *
 * @example
 * // Full-width with helper text
 * <WingsTextArea
 *   label="Notes"
 *   helpText="Max 500 characters"
 *   fullWidth
 *   rows={5}
 *   value={notes}
 *   onChange={(e) => setNotes(e.target.value)}
 * />
 */
const WingsTextArea = ({
  label,
  helpText,
  error,
  id,
  size = 'medium',
  fullWidth = false,
  autoResize = false,
  rows = 3,
  disabled = false,
  className = '',
  ...props
}) => {
  const textareaId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined)

  const wrapperClasses = [
    'wings-textarea-wrapper',
    fullWidth && 'wings-textarea-wrapper--full',
    error && 'wings-textarea-wrapper--error',
    className,
  ].filter(Boolean).join(' ')

  const textareaClasses = [
    'wings-textarea',
    `wings-textarea--${size}`,
    error && 'wings-textarea--error',
    disabled && 'wings-textarea--disabled',
  ].filter(Boolean).join(' ')

  return (
    <div className={wrapperClasses}>
      {label && (
        <label className="wings-textarea-label" htmlFor={textareaId}>
          {label}
        </label>
      )}
      <InputTextarea
        id={textareaId}
        className={textareaClasses}
        autoResize={autoResize}
        rows={rows}
        disabled={disabled}
        aria-describedby={helpText || error ? `${textareaId}-hint` : undefined}
        aria-invalid={!!error}
        {...props}
      />
      {(helpText || error) && (
        <span
          id={`${textareaId}-hint`}
          className={`wings-textarea-hint${error ? ' wings-textarea-hint--error' : ''}`}
        >
          {error || helpText}
        </span>
      )}
    </div>
  )
}

WingsTextArea.propTypes = {
  /** Label displayed above the textarea */
  label: PropTypes.string,
  /** Helper text displayed below the textarea */
  helpText: PropTypes.string,
  /** Error message — puts the textarea in an error state and replaces helpText */
  error: PropTypes.string,
  /** HTML id for the textarea; auto-generated from label if omitted */
  id: PropTypes.string,
  /** Size variant controlling font size and padding */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Expands the textarea to fill its container width */
  fullWidth: PropTypes.bool,
  /** Automatically resizes vertically to fit content */
  autoResize: PropTypes.bool,
  /** Initial number of visible text rows */
  rows: PropTypes.number,
  /** Disables the textarea */
  disabled: PropTypes.bool,
  /** Additional CSS classes applied to the wrapper element */
  className: PropTypes.string,
  /** Current value of the textarea */
  value: PropTypes.string,
  /** Change handler — receives the native event */
  onChange: PropTypes.func,
  /** Placeholder text */
  placeholder: PropTypes.string,
}

export default WingsTextArea
